import type { InjectionKey, MaybeRefOrGetter, PropType, Ref } from "vue";

import { omit } from "radash";
import {
  computed,
  inject,
  nextTick,
  provide,
  ref,
  toValue,
  watchEffect,
} from "vue";

import type { DefineProps } from "@/lib/composables/componentComposable";

import { propsDefinition } from "@/lib/composables/componentComposable";

type ServerSideErrors = Record<string, string[]>;
type ValidationProvider = {
  blockingRulesValid: boolean;
  isValidating: boolean;
  name: string;
  validateAll: () => Promise<void>;
};

const _validationObserverKey = Symbol() as InjectionKey<{
  addProvider: (name: string, provider: ValidationProvider) => void;
  deleteProvider: (name: string) => void;
  resetServerSideErrors: (name: string) => void;
  serverSideErrors: Ref<ServerSideErrors>;
}>;

const useValidationObserverProps = propsDefinition({
  serverSideErrors: {
    type: Object as PropType<ServerSideErrors>,
    default: () => ({}),
  },
});

function useValidationObserver(
  props?: DefineProps<typeof useValidationObserverProps>,
) {
  const parentValidationObserver = inject(_validationObserverKey, null);
  /*
    Server side errors
   */
  const storedServerSideErrors = ref<ServerSideErrors>({});

  watchEffect(() => {
    storedServerSideErrors.value = props?.serverSideErrors ?? {};
  });

  function resetServerSideErrors(name: string) {
    if (storedServerSideErrors.value[name]) {
      delete storedServerSideErrors.value[name];
    }
  }

  /*
    Providers
   */
  const observedProviders = ref<Record<string, ValidationProvider>>({});

  function addProvider(name: string, provider: ValidationProvider) {
    observedProviders.value[name] = provider;
    parentValidationObserver?.addProvider(name, provider);
  }

  function deleteProvider(name: string) {
    observedProviders.value = omit(observedProviders.value, [name]);
    parentValidationObserver?.deleteProvider(name);
  }

  provide(_validationObserverKey, {
    serverSideErrors: storedServerSideErrors,
    addProvider,
    deleteProvider,
    resetServerSideErrors,
  });

  /*
    Validation
   */
  function getState(providers: Ref<ValidationProvider[]>) {
    return {
      valid: computed(() => {
        return providers.value.every((provider) => provider.blockingRulesValid);
      }),
      invalid: computed(() => {
        return providers.value.some((provider) => !provider.blockingRulesValid);
      }),
      isValidating: computed(() => {
        return providers.value.some((provider) => provider.isValidating);
      }),
    };
  }

  const { valid, invalid, isValidating } = getState(
    computed(() => Object.values(observedProviders.value)),
  );

  function getPartialState(names: MaybeRefOrGetter<string[]>) {
    const partialProviders = computed(() => {
      return Object.values(observedProviders.value).filter((provider) =>
        toValue(names).includes(provider.name),
      );
    });

    return getState(partialProviders);
  }

  async function validateAll() {
    const validationPromises = Object.values(observedProviders.value).map(
      (provider) => provider.validateAll(),
    );
    await Promise.all(validationPromises);
    await nextTick(); // Wait for validation to update in providers
    return valid.value;
  }

  async function handleSubmit(callback: () => void) {
    if (await validateAll()) {
      callback();
    }
  }

  return {
    validateAll,
    handleSubmit,
    isValidating,
    valid,
    invalid,
    getPartialState,
  };
}

export {
  _validationObserverKey,
  useValidationObserver,
  useValidationObserverProps,
};
